import * as React from 'react';
import {
  DataGridPro,
  GridColumnVisibilityModel,
  LicenseInfo,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import config from 'config';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import {
  fetchPerDiemReportStatuses,
  fetchPickupAppointments,
  fetchTerminalStatuses,
  fetchTriggerPickupAppointments,
  WatchList,
} from 'services/clients/watchList';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { WATCH_LIST_QUERY_PARAMS } from 'pages/TrackAndTrace/constants';
import { Appointment, PerDiemReportStatus } from 'types/Watchlist';
import { useNavHeightContext } from 'common/contexts/NavHeightContext';
import { brandColors } from 'brandColors';
import { useContainerSelection } from 'common/hooks/container-selection';
import { usePermissions } from 'utils/permissions';
import {
  REFRESH_PER_DIEM_STATUS_INTERVAL_MS,
  REFRESH_PICKUP_APPOINTMENTS_INTERVAL_MS,
} from '../../config';
import { getColumns } from './columns';
import {
  PerDiemReportStatuses,
  PerDiemReportStatusesContext,
  PickupAppointments,
  ContainerAppointmentsContext,
  ReturnAppointments,
  ReturnAppointmentsContext,
  TriggerPickupAppointments,
  TriggerPickupAppointmentsContext,
} from './context';
import { pinMuiDataGridHeaders } from '../../../../utils/dataGrid';
import { anyFiltersOn } from '../controls/filters/utils';
import { SelectionToolbar } from '../../../../components/ContainerActions/SelectionToolbar';
import { MagicCheckbox } from '../../../../components/ContainerActions/MagicCheckbox';
import { ExtraScrollBar } from './ExtraScrollbar';
import { AddContainersOverlay, NoContainersFoundOverlay } from './table';

LicenseInfo.setLicenseKey(config.DATA_GRID_PRO_LICENSE_KEY);
interface Props {
  openAddContainersModal: () => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  watchListQuery: UseQueryResult<WatchList>;
}

/**
 * This component renders the entirety of the containers table
 */
export const ContainerTable: React.FC<Props> = (props) => {
  const { columnVisibilityModel, openAddContainersModal, watchListQuery } = props;
  const permissions = usePermissions();
  const columns = React.useMemo(() => getColumns(permissions), [permissions]);
  const dataGridApiRef = useGridApiRef();

  const { searchParams, setSearchParams } = useCustomSearchParams(WATCH_LIST_QUERY_PARAMS);

  // LEGACY PINNING
  const { marginTop } = useNavHeightContext();
  const [isFixedHeaders, setIsFixedHeaders] = React.useState(false);
  React.useEffect(() => {
    window.addEventListener(
      'scroll',
      pinMuiDataGridHeaders.bind(null, setIsFixedHeaders, isFixedHeaders),
      { passive: true }
    );

    return () => window.removeEventListener('scroll', pinMuiDataGridHeaders);
  }, [isFixedHeaders]);

  const { data: watchList, isLoading, error } = watchListQuery;
  const containers = watchList?.results;

  const appointmentsQuery = useQuery(
    ['pickupAppointments', searchParams, watchList?.results],
    ({ signal }) =>
      fetchPickupAppointments({
        containers: containers || [],
        pageSize: searchParams.pageSize,
        signal,
      }),
    {
      enabled: !!containers && containers.length > 0,
      refetchInterval: REFRESH_PICKUP_APPOINTMENTS_INTERVAL_MS,
    }
  );

  const triggerPickupAppointmentsQuery = useQuery(
    ['triggerPickupAppointments', searchParams, watchList?.results],
    ({ signal }) =>
      fetchTriggerPickupAppointments({
        containers: containers || [],
        pageSize: searchParams.pageSize,
        signal,
      }),
    {
      enabled: !!containers && containers.length > 0,
      refetchInterval: REFRESH_PICKUP_APPOINTMENTS_INTERVAL_MS,
    }
  );

  const perDiemReportStatusQuery = useQuery(
    ['perDiemReportStatuses', searchParams, watchList?.results],
    ({ signal }) =>
      fetchPerDiemReportStatuses({
        containers: containers || [],
        pageSize: searchParams.pageSize,
        signal,
      }),
    {
      enabled: !!containers && containers.length > 0,
      refetchInterval: REFRESH_PER_DIEM_STATUS_INTERVAL_MS,
    }
  );

  const returnAppointmentsQuery = useQuery(
    ['terminalStatuses', searchParams],
    ({ signal }) => fetchTerminalStatuses({ containers: containers || [], signal }),
    { enabled: !!containers && containers.length > 0 }
  );

  const containerAppointments: PickupAppointments = React.useMemo(() => {
    const appointments: Record<string, Appointment> = {};
    const { data, status, isFetching } = appointmentsQuery;
    data?.forEach((appointment) => {
      appointments[appointment.containerNumber] = appointment;
    });

    return { appointments, status, isFetching };
  }, [appointmentsQuery]);

  const perDiemReportStatues: PerDiemReportStatuses = React.useMemo(() => {
    const reportStatuses: Record<string, PerDiemReportStatus> = {};
    const { data, status, refetch } = perDiemReportStatusQuery;
    data?.forEach((reportStatus) => {
      reportStatuses[reportStatus.containerNumber] = reportStatus;
    });

    return { reportStatuses, status, refetch };
  }, [perDiemReportStatusQuery]);

  const returnAppointments: ReturnAppointments = React.useMemo(() => {
    const { data, status } = returnAppointmentsQuery;
    return {
      appointments: data?.appointments || {},
      terminalStatuses: data?.terminalStatuses || {},
      status,
    };
  }, [returnAppointmentsQuery]);

  const triggerPickupAppointments: TriggerPickupAppointments = React.useMemo(() => {
    const { data, status } = triggerPickupAppointmentsQuery;
    return { triggerPickupAppointments: data || {}, status };
  }, [triggerPickupAppointmentsQuery]);

  const NoResultsOrRowsOverlay =
    anyFiltersOn(searchParams) || searchParams.containerNumbers.length > 0
      ? NoContainersFoundOverlay
      : AddContainersOverlay;

  const { handleOnSelectionModelChange, selectionModel, selection } = useContainerSelection(
    (c) => c.containerNumber,
    watchList
  );

  return (
    <div>
      <ReturnAppointmentsContext.Provider value={returnAppointments}>
        <PerDiemReportStatusesContext.Provider value={perDiemReportStatues}>
          <ContainerAppointmentsContext.Provider value={containerAppointments}>
            <TriggerPickupAppointmentsContext.Provider value={triggerPickupAppointments}>
              <ExtraScrollBar
                isFixedHeaders={isFixedHeaders}
                dataGridApiRef={dataGridApiRef}
                stylesOverride={{ background: brandColors.colors.blue[600] }}
              />
              <DataGridPro
                apiRef={dataGridApiRef}
                rowCount={watchList?.count || 0}
                columns={columns}
                onSelectionModelChange={handleOnSelectionModelChange}
                // selectionModel={selectedContainers}
                rows={watchList?.results || []}
                rowHeight={90}
                autoHeight
                checkboxSelection
                disableSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                pinnedColumns={{
                  left: ['__check__', 'containerNumber', 'state'],
                  right: ['actions'],
                }}
                getRowId={(r) => r.containerNumber}
                pagination
                paginationMode="server"
                page={searchParams.page - 1}
                onPageChange={(page: number) => setSearchParams({ page: page + 1 })}
                pageSize={searchParams.pageSize}
                onPageSizeChange={(pageSize: number) => setSearchParams({ pageSize })}
                rowsPerPageOptions={[25, 50, 75, 100]}
                loading={isLoading}
                error={error}
                headerHeight={32}
                sortingMode="server"
                selectionModel={selectionModel}
                components={{
                  NoRowsOverlay: NoResultsOrRowsOverlay,
                  Toolbar: selection.model !== 'none' ? SelectionToolbar : null,
                  BaseCheckbox: MagicCheckbox,
                }}
                componentsProps={{
                  noRowsOverlay: {
                    openAddContainersModal,
                  },
                }}
                classes={{
                  virtualScroller: `${selection.model !== 'none' ? 'pt-8' : ''} scrollbar`,
                }}
                // eslint-disable-next-line
                // @ts-ignore
                sx={{
                  '& .MuiDataGrid-columnHeaders': isFixedHeaders
                    ? {
                        top: marginTop + 16, // Navbar height + extra scrollbar
                        left: '1px',
                        zIndex: 499, // Container details page is 500
                        position: 'fixed',
                      }
                    : undefined,
                  '.MuiDataGrid-pinnedColumnHeaders--right': {
                    paddingRight: '0 !important',
                  },
                  '.MuiDataGrid-pinnedColumnHeaders--right div.w-full.h-8': {
                    border: 'none',
                  },
                  '.MuiDataGrid-checkboxInput.Mui-checked': {
                    color: `${brandColors.colors.blue.DEFAULT} !important`,
                  },
                  '.MuiDataGrid-checkboxInput': {
                    color: `${brandColors.colors.gray[500]} !important`,
                  },
                  '& .bg-orange-50.px-4.w-screen': {
                    // Bar that appears when you select a row
                    top: '2rem !important',
                  },
                }}
                disableColumnFilter
              />
            </TriggerPickupAppointmentsContext.Provider>
          </ContainerAppointmentsContext.Provider>
        </PerDiemReportStatusesContext.Provider>
      </ReturnAppointmentsContext.Provider>
    </div>
  );
};
ContainerTable.displayName = 'TrackAndTrace.ContainerTable';
